import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { LoadingComponent } from '@components/loading/loading.component';
import { ReactiveFormsModule } from '@angular/forms';
import { SearchService } from '@services/search/search-service';
import { SearchPreviewComponent } from '@components/search/search-bar/search-preview/search-preview.component';
import { TranslateModule } from '@ngx-translate/core';
import { ClickOutsideDirective } from '@components/misc/aw-form-creator/directives/click-outside.directive';
import { SidebarService } from '@services/sidebar/sidebar.service';

@Component({
    selector: 'app-search-bar',
    imports: [LoadingComponent, ClickOutsideDirective, ReactiveFormsModule, SearchPreviewComponent, TranslateModule],
    templateUrl: './search-bar.component.html',
    styleUrl: './search-bar.component.scss'
})
export class SearchBarComponent {
    @ViewChild('searchInput', { static: false }) searchInput: ElementRef;

    suggestionShortCode: string;
    isMac: boolean = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac');

    /**
     * HostListener for keyboard events.
     * Focuses the search bar when 'k' is pressed with Ctrl or Meta (CMD) key.
     *
     * @param event - The keyboard event triggered on the document.
     */
    @HostListener('document:keydown', ['$event'])
    handleGlobalKeydown(event: KeyboardEvent) {
        if (event.key?.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            if (!this.searchService.isSearchDropdownOpened()) {
                this.searchService.isSearchDropdownOpened.set(true);
                this.searchInput.nativeElement.focus();
                this.searchInput.nativeElement.select();
            } else {
                // If the search dropdown is already open, just focus the input
                this.searchService.isSearchDropdownOpened.set(false);
                this.searchInput.nativeElement.blur();
            }
        }
    }

    constructor(
        protected searchService: SearchService,
        protected sidebarService: SidebarService
    ) {}

    async startFuzzySearch() {
        this.searchService.searchQuery.set(this.searchService.searchInputValue());
        this.searchService.searchInputValue.set(this.searchService.phoneNumberFormat(this.searchService.searchInputValue()));
        this.searchInput.nativeElement.focus();
        this.searchService.isSearchDropdownOpened.set(true);
        await this.searchService.performFuzzySearch(this.searchService.searchQuery());
    }

    onSearchInputChange(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.searchService.searchInputValue.set(value);
        this.startFuzzySearch();
    }

    toggleSearchDropdown() {
        this.searchService.isSearchDropdownOpened.set(true);
    }

    handleItemClick(event: any) {
        if (event) {
            this.searchService.isSearchDropdownOpened.set(false);
            this.searchInput.nativeElement.blur();
        }
    }

    handleSuggestion(shortCode: string) {
        this.suggestionShortCode = shortCode;
        this.searchService.searchInputValue.set(shortCode);
        this.searchService.searchQuery.set(shortCode);
        this.searchInput.nativeElement.focus();
    }

    toggleMobileSearchOpen() {
        this.searchService.mobileSearchOpen.set(!this.searchService.mobileSearchOpen());
        if (!this.searchService.mobileSearchOpen()) {
            this.searchService.isSearchDropdownOpened.set(false);
        } else {
            // Focus the search input element after opening the mobile search
            // This is done with a timeout to ensure the DOM is ready (cdr does not work here)
            setTimeout(() => {
                const el: HTMLElement = document.getElementById('search-input-id');
                el.focus();
            }, 0);
        }
    }
}
